/**
 * Crisp chat widget loader.
 *
 * Activates when `VITE_CRISP_WEBSITE_ID` is set in the Vite env.
 * Injects the official Crisp snippet once per page load and populates
 * `$crisp` / `CRISP_WEBSITE_ID` globals as Crisp's docs prescribe.
 *
 * `identifyCrispUser()` pushes the signed-in user's email + tenant so
 * agents see who they're talking to. Call on login + on every app
 * boot where the user is already authenticated.
 *
 * `resetCrisp()` logs the current user out of the widget on signout.
 *
 * All three helpers are no-ops when the env var is missing, mirroring
 * the analytics / error-tracking pattern.
 */

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
  }
}

let loaded = false;

function crispId(): string | null {
  const id = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;
  return id && id.trim() ? id.trim() : null;
}

export function initCrisp(): void {
  if (loaded) return;
  const id = crispId();
  if (!id) return;
  if (typeof window === 'undefined') return;

  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = id;

  const script = document.createElement('script');
  script.src = 'https://client.crisp.chat/l.js';
  script.async = true;
  document.head.appendChild(script);
  loaded = true;
}

function pushCrisp(...entry: unknown[]): void {
  if (!crispId() || typeof window === 'undefined') return;
  window.$crisp = window.$crisp || [];
  (window.$crisp as unknown[]).push(entry);
}

export function identifyCrispUser(user: {
  email?: string;
  name?: string;
  tenantId?: string;
  userId?: string;
}): void {
  if (user.email) pushCrisp('set', 'user:email', [user.email]);
  if (user.name) pushCrisp('set', 'user:nickname', [user.name]);
  const data: Record<string, string> = {};
  if (user.tenantId) data.tenant_id = user.tenantId;
  if (user.userId) data.user_id = user.userId;
  if (Object.keys(data).length > 0) {
    pushCrisp('set', 'session:data', [Object.entries(data)]);
  }
}

export function resetCrisp(): void {
  pushCrisp('do', 'session:reset');
}
